import {useEffect, useMemo, useState} from 'react';
import {createPortal} from 'react-dom';
import {startOfMonth} from 'date-fns';
import useStore from '../../store/store';
import {hoursSelect} from '../../const/const';
import {
    ensureMinRentFinish,
    fetchCarPrices,
    formatCellPrice,
    formatDayKey,
    formatMonthTitle,
    formatRangeDates,
    getDayPriceInfo,
    getLoyaltySalePercent,
    getMinStartHourForDate,
    getPersonalSalePercent,
    getSupersaleBounds,
    getMonthDays,
    isInRange,
    isPastDay,
    isSameDaySafe,
    isTooEarlyFinishDay,
    shiftMonth,
    WEEKDAYS,
} from '../../utils/calendar';
import styles from './rent-calendar.module.scss';

const Chevron = ({dir}) => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        {dir === 'left' ? (
            <path d="M10.5 3.5L5.5 8l5 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        ) : (
            <path d="M5.5 3.5L10.5 8l-5 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        )}
    </svg>
);

const SuperSaleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
        <path
            d="M3.83787 0.712604L3.41787 0.460938L3.33787 0.943938C3.21053 1.7066 2.68953 2.41527 2.11487 2.91327C0.989866 3.8886 0.743533 4.9486 1.09187 5.8236C1.4252 6.66094 2.2712 7.23427 3.12987 7.32994L3.32853 7.35194C2.8372 7.0516 2.52187 6.34994 2.63187 5.8256C2.74053 5.31027 3.1112 4.8276 3.8232 4.38227L4.1822 4.15827L4.3162 4.55994C4.3952 4.79727 4.53187 4.98794 4.67087 5.1816C4.73753 5.27494 4.8052 5.36927 4.8672 5.4696C5.08153 5.81794 5.1382 6.20527 4.99987 6.5896C4.87387 6.93894 4.6662 7.2136 4.37987 7.36594L4.7032 7.32994C5.5092 7.24027 6.10087 6.9646 6.4852 6.5036C6.8662 6.0466 6.99987 5.45827 6.99987 4.83194C6.99987 4.2486 6.7602 3.64727 6.47753 3.14694C6.1462 2.56094 5.71387 2.07427 5.24187 1.6026C5.1602 1.76594 5.16653 1.83194 4.99853 2.09427C4.78037 1.51492 4.37088 1.02746 3.83787 0.712604Z"
            fill="white"
        />
    </svg>
);

const LoyaltyInfoIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
            d="M9.16602 9.14656C9.16602 8.92555 9.25381 8.71359 9.41009 8.55731C9.56637 8.40103 9.77834 8.31323 9.99935 8.31323C10.2204 8.31323 10.4323 8.40103 10.5886 8.55731C10.7449 8.71359 10.8327 8.92555 10.8327 9.14656V14.1466C10.8327 14.3676 10.7449 14.5795 10.5886 14.7358C10.4323 14.8921 10.2204 14.9799 9.99935 14.9799C9.77834 14.9799 9.56637 14.8921 9.41009 14.7358C9.25381 14.5795 9.16602 14.3676 9.16602 14.1466V9.14656ZM9.99935 5.03906C9.77834 5.03906 9.56637 5.12686 9.41009 5.28314C9.25381 5.43942 9.16602 5.65138 9.16602 5.8724C9.16602 6.09341 9.25381 6.30537 9.41009 6.46165C9.56637 6.61793 9.77834 6.70573 9.99935 6.70573C10.2204 6.70573 10.4323 6.61793 10.5886 6.46165C10.7449 6.30537 10.8327 6.09341 10.8327 5.8724C10.8327 5.65138 10.7449 5.43942 10.5886 5.28314C10.4323 5.12686 10.2204 5.03906 9.99935 5.03906Z"
            fill="#243C4E"
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M9.99935 1.66406C5.39685 1.66406 1.66602 5.3949 1.66602 9.9974C1.66602 14.5999 5.39685 18.3307 9.99935 18.3307C14.6018 18.3307 18.3327 14.5999 18.3327 9.9974C18.3327 5.3949 14.6018 1.66406 9.99935 1.66406ZM3.33268 9.9974C3.33268 11.7655 4.03506 13.4612 5.2853 14.7114C6.53555 15.9617 8.23124 16.6641 9.99935 16.6641C11.7675 16.6641 13.4632 15.9617 14.7134 14.7114C15.9636 13.4612 16.666 11.7655 16.666 9.9974C16.666 8.22929 15.9636 6.53359 14.7134 5.28335C13.4632 4.03311 11.7675 3.33073 9.99935 3.33073C8.23124 3.33073 6.53555 4.03311 5.2853 5.28335C4.03506 6.53359 3.33268 8.22929 3.33268 9.9974Z"
            fill="#243C4E"
        />
    </svg>
);

function hasLoyaltyCardPercent(user) {
    return getLoyaltySalePercent(user) > 0;
}

function HourColumn({label, value, onChange, minHour = 0}) {
    const hour = value == null ? minHour : value;

    return (
        <div className={styles.hourCol}>
            <div className={styles.hourLabel}>{label}</div>
            <select
                className={styles.hourSelect}
                value={hour}
                onChange={(e) => onChange(parseInt(e.target.value, 10))}
            >
                {hoursSelect.map((item, index) => (
                    <option key={item} value={index} disabled={index < minHour}>
                        {item}
                    </option>
                ))}
            </select>
        </div>
    );
}

function MonthHead({
    monthDate,
    lang,
    canPrev,
    canNext,
    onPrev,
    onNext,
    prevLabel,
    nextLabel,
    className,
}) {
    return (
        <div className={className || styles.monthHead}>
            <button type="button" className={`${styles.nav} ${styles.navPrev}`} disabled={!canPrev} aria-label={prevLabel} onClick={onPrev}>
                <Chevron dir="left"/>
            </button>
            <div className={styles.monthHeadTitle}>{formatMonthTitle(monthDate, lang)}</div>
            <button type="button" className={`${styles.nav} ${styles.navNext}`} disabled={!canNext} aria-label={nextLabel} onClick={onNext}>
                <Chevron dir="right"/>
            </button>
        </div>
    );
}

function MonthGrid({
    monthDate,
    lang,
    currentDate,
    draftStart,
    draftFinish,
    daysPrices,
    promoStartKey,
    promoEndKey,
    minRentDays,
    salePercent,
    onDayClick,
}) {
    const days = useMemo(() => getMonthDays(monthDate), [monthDate]);
    const weekdays = WEEKDAYS[lang] || WEEKDAYS.ru;
    const pickingFinish = !!draftStart && !draftFinish;

    return (
        <div className={styles.month}>
            <div className={styles.weekdays}>
                {weekdays.map((day) => (
                    <div key={day} className={styles.weekday}>{day}</div>
                ))}
            </div>
            <div className={styles.days}>
                {days.map((date, index) => {
                    if (!date) {
                        return <div key={`e-${index}`} className={styles.dayEmpty}/>;
                    }

                    const dayKey = formatDayKey(date);
                    const past = isPastDay(date, currentDate);
                    const isCurrentDay = !past && isSameDaySafe(date, currentDate);
                    const tooEarlyFinish = pickingFinish && isTooEarlyFinishDay(date, draftStart, minRentDays);
                    const disabled = past || tooEarlyFinish;
                    const {price, isPromo, isSale} = getDayPriceInfo(date, daysPrices, salePercent);
                    const showBadge = !disabled && isPromo && dayKey === promoStartKey;
                    const isSelectedStart = !past && isSameDaySafe(date, draftStart);
                    const isSelectedEnd = !past && isSameDaySafe(date, draftFinish);
                    const selected = isSelectedStart || isSelectedEnd;
                    const inRange = isInRange(date, draftStart, draftFinish);
                    const inSelection = selected || inRange;
                    const isPromoStartDay = !disabled && isPromo && dayKey === promoStartKey;
                    const isPromoEndDay = !disabled && isPromo && dayKey === promoEndKey;
                    const showPromoStart = isPromoStartDay && (!inSelection || isSelectedStart);
                    const showPromoEnd = isPromoEndDay && (!inSelection || isSelectedEnd);
                    const showSelectedStart = isSelectedStart && (!isPromo || isPromoStartDay);
                    const showSelectedEnd = isSelectedEnd && (!isPromo || isPromoEndDay);
                    const className = [
                        styles.day,
                        disabled && styles.dayDisabled,
                        isCurrentDay && styles.dayCurrent,
                        !disabled && !selected && inRange && styles.dayInRange,
                        selected && styles.daySelected,
                        showSelectedStart && styles.daySelectedStart,
                        showSelectedEnd && styles.daySelectedEnd,
                        showBadge && styles.dayWithBadge,
                        !disabled && isPromo && styles.dayPromo,
                        !disabled && !isPromo && isSale && styles.daySale,
                        showPromoStart && styles.dayPromoStart,
                        showPromoEnd && styles.dayPromoEnd,
                    ].filter(Boolean).join(' ');

                    return (
                        <button
                            key={`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`}
                            type="button"
                            className={className}
                            disabled={disabled}
                            onClick={() => onDayClick(date)}
                        >
                            {showBadge && (
                                <span className={styles.superSaleBadge}>
                                    <SuperSaleIcon/>
                                    <span>Super Sale</span>
                                </span>
                            )}
                            <span className={styles.dayNum}>{date.getDate()}</span>
                            {!!price && (
                                <span className={styles.dayPrice}>{formatCellPrice(price)}</span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function RentCalendar({
    open,
    onClose,
    minRentDays,
    crmCarId,
}) {
    const lang = useStore((state) => state.lang);
    const currentDate = useStore((state) => state.currentDate);
    const carId = useStore((state) => state.carId);
    const user = useStore((state) => state.user);
    const promoCodeSale = useStore((state) => state.promoCodeSale);
    const isApplyPromoCode = useStore((state) => state.isApplyPromoCode);
    const disableOtherDiscount = useStore((state) => state.disableOtherDiscount);
    const startDate = useStore((state) => state.startDate);
    const finishDate = useStore((state) => state.finishDate);
    const startHours = useStore((state) => state.startHours);
    const finishHours = useStore((state) => state.finishHours);
    const setStartDate = useStore((state) => state.setStartDate);
    const setFinishDate = useStore((state) => state.setFinishDate);
    const setStartHours = useStore((state) => state.setStartHours);
    const setFinishHours = useStore((state) => state.setFinishHours);

    const [viewMonth, setViewMonth] = useState(() => startOfMonth(currentDate || new Date()));
    const [draftStart, setDraftStart] = useState(startDate);
    const [draftFinish, setDraftFinish] = useState(finishDate);
    const [draftStartHours, setDraftStartHours] = useState(startHours);
    const [draftFinishHours, setDraftFinishHours] = useState(finishHours);
    const [daysPrices, setDaysPrices] = useState({});
    const [pricesLoading, setPricesLoading] = useState(false);

    const minMonth = startOfMonth(currentDate || new Date());
    const maxMonth = shiftMonth(minMonth, 11);
    const rightMonth = shiftMonth(viewMonth, 1);
    const canPrev = viewMonth.getTime() > minMonth.getTime();
    const canNext = rightMonth.getTime() < maxMonth.getTime();
    const {startKey: promoStartKey, endKey: promoEndKey} = useMemo(
        () => getSupersaleBounds(daysPrices, currentDate),
        [daysPrices, currentDate]
    );

    const minStartHour = useMemo(
        () => getMinStartHourForDate(draftStart, currentDate),
        [draftStart, currentDate]
    );

    const minFinishHour = useMemo(() => {
        if (!draftStart || !draftFinish || !isSameDaySafe(draftStart, draftFinish)) {
            return 0;
        }
        return draftStartHours == null ? minStartHour : draftStartHours;
    }, [draftStart, draftFinish, draftStartHours, minStartHour]);

    useEffect(() => {
        if (!open) {
            return;
        }
        setDraftStart(startDate);
        setDraftFinish(finishDate);
        setDraftStartHours(startHours);
        setDraftFinishHours(finishHours);
        const base = startOfMonth(startDate || currentDate || new Date());
        setViewMonth(base.getTime() < minMonth.getTime() ? minMonth : base);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const onKey = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('keydown', onKey);

        return () => {
            document.body.style.overflow = prevOverflow;
            document.removeEventListener('keydown', onKey);
        };
    }, [open, onClose]);

    useEffect(() => {
        if (draftStartHours == null || draftStartHours < minStartHour) {
            setDraftStartHours(minStartHour);
        }
    }, [minStartHour, draftStartHours]);

    useEffect(() => {
        if (draftFinishHours == null || draftFinishHours < minFinishHour) {
            setDraftFinishHours(minFinishHour);
        }
    }, [minFinishHour, draftFinishHours]);

    useEffect(() => {
        if (!open || (!carId && !crmCarId)) {
            return undefined;
        }

        const ac = new AbortController();
        const {dtstart, dtend} = formatRangeDates(viewMonth);
        setPricesLoading(true);
        setDaysPrices({});

        fetchCarPrices({id: carId, carid: crmCarId, dtstart, dtend, signal: ac.signal})
            .then((days) => {
                if (!ac.signal.aborted) {
                    setDaysPrices(days);
                }
            })
            .catch((error) => {
                if (error?.name === 'AbortError') {
                    return;
                }
                console.error(error.message);
                setDaysPrices({});
            })
            .finally(() => {
                if (!ac.signal.aborted) {
                    setPricesLoading(false);
                }
            });

        return () => {
            ac.abort();
        };
    }, [open, viewMonth, carId, crmCarId]);

    const showLoyaltyNotice = hasLoyaltyCardPercent(user);
    const exclusivePromo = disableOtherDiscount && isApplyPromoCode;
    const salePercent = getPersonalSalePercent(user, promoCodeSale, {exclusivePromo}).percent;

    if (!open) {
        return null;
    }

    const t = {
        startTime: lang === 'en' ? 'Start time' : 'Время начала',
        finishTime: lang === 'en' ? 'Return time' : 'Время возврата',
        apply: lang === 'en' ? 'Apply' : 'Применить',
        prev: lang === 'en' ? 'Previous months' : 'Предыдущие месяцы',
        next: lang === 'en' ? 'Next months' : 'Следующие месяцы',
        loadingPrices: lang === 'en' ? 'Loading prices' : 'Загрузка цен',
        loyaltyPrice: lang === 'en'
            ? 'Your price includes a personal loyalty program discount'
            : 'Ваша цена с учетом личной скидки по программе лояльности',
    };

    const onDayClick = (date) => {
        if (!draftStart || (draftStart && draftFinish)) {
            setDraftStart(date);
            setDraftFinish(null);
            return;
        }
        if (date < draftStart) {
            setDraftStart(date);
            setDraftFinish(ensureMinRentFinish(date, draftStart, minRentDays));
            return;
        }
        setDraftFinish(ensureMinRentFinish(draftStart, date, minRentDays));
    };

    const handleApply = () => {
        if (!draftStart) {
            onClose();
            return;
        }

        const finish = ensureMinRentFinish(draftStart, draftFinish, minRentDays);
        const startHour = draftStartHours == null ? minStartHour : Math.max(draftStartHours, minStartHour);
        const finishHourBase = draftFinishHours == null ? 0 : draftFinishHours;
        const finishHour = isSameDaySafe(draftStart, finish)
            ? Math.max(finishHourBase, startHour)
            : finishHourBase;

        setStartDate(draftStart);
        setFinishDate(finish);
        setStartHours(startHour);
        setFinishHours(finishHour);
        onClose();
    };

    return createPortal(
        <div className={styles.overlay} onClick={onClose} role="presentation">
            <div
                className={styles.dialog}
                role="dialog"
                aria-modal="true"
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.head}>
                    <button type="button" className={`${styles.nav} ${styles.navPrev}`} disabled={!canPrev} aria-label={t.prev} onClick={() => setViewMonth(shiftMonth(viewMonth, -1))}>
                        <Chevron dir="left"/>
                    </button>
                    <div className={`${styles.monthTitle} ${styles.monthTitleFirst}`}>{formatMonthTitle(viewMonth, lang)}</div>
                    <div className={`${styles.monthTitle} ${styles.monthTitleSecond}`}>{formatMonthTitle(rightMonth, lang)}</div>
                    <button type="button" className={`${styles.nav} ${styles.navNext}`} disabled={!canNext} aria-label={t.next} onClick={() => setViewMonth(shiftMonth(viewMonth, 1))}>
                        <Chevron dir="right"/>
                    </button>
                </div>
                <div className={`${styles.monthsWrap} ${pricesLoading ? styles.monthsBusy : ''}`} aria-busy={pricesLoading}>
                    <div className={styles.months}>
                        <div className={styles.monthCol}>
                            <MonthHead
                                monthDate={viewMonth}
                                lang={lang}
                                canPrev={canPrev}
                                canNext={canNext}
                                onPrev={() => setViewMonth(shiftMonth(viewMonth, -1))}
                                onNext={() => setViewMonth(shiftMonth(viewMonth, 1))}
                                prevLabel={t.prev}
                                nextLabel={t.next}
                            />
                            <MonthGrid
                                monthDate={viewMonth}
                                lang={lang}
                                currentDate={currentDate}
                                draftStart={draftStart}
                                draftFinish={draftFinish}
                                daysPrices={daysPrices}
                                promoStartKey={promoStartKey}
                                promoEndKey={promoEndKey}
                                minRentDays={minRentDays}
                                salePercent={salePercent}
                                onDayClick={onDayClick}
                            />
                        </div>
                        <div className={styles.monthCol}>
                            <MonthHead
                                monthDate={rightMonth}
                                lang={lang}
                                canPrev={canPrev}
                                canNext={canNext}
                                onPrev={() => setViewMonth(shiftMonth(viewMonth, -1))}
                                onNext={() => setViewMonth(shiftMonth(viewMonth, 1))}
                                prevLabel={t.prev}
                                nextLabel={t.next}
                            />
                            <MonthGrid
                                monthDate={rightMonth}
                                lang={lang}
                                currentDate={currentDate}
                                draftStart={draftStart}
                                draftFinish={draftFinish}
                                daysPrices={daysPrices}
                                promoStartKey={promoStartKey}
                                promoEndKey={promoEndKey}
                                minRentDays={minRentDays}
                                salePercent={salePercent}
                                onDayClick={onDayClick}
                            />
                        </div>
                    </div>
                    {pricesLoading && (
                        <div className={styles.pricesLoader}>
                            <span className={styles.spinner} aria-hidden="true"/>
                            <span>{t.loadingPrices}</span>
                        </div>
                    )}
                </div>
                {showLoyaltyNotice && (
                    <div className={styles.loyaltyNotice}>
                        <span className={styles.loyaltyNoticeIcon}>
                            <LoyaltyInfoIcon/>
                        </span>
                        <span className={styles.loyaltyNoticeText}>{t.loyaltyPrice}</span>
                    </div>
                )}
                <div className={styles.hours}>
                    <HourColumn
                        label={t.startTime}
                        value={draftStartHours}
                        minHour={minStartHour}
                        onChange={setDraftStartHours}
                    />
                    <HourColumn
                        label={t.finishTime}
                        value={draftFinishHours}
                        minHour={minFinishHour}
                        onChange={setDraftFinishHours}
                    />
                </div>
                <button type="button" className={styles.apply} onClick={handleApply}>
                    {t.apply}
                </button>
            </div>
        </div>,
        document.body
    );
}

export default RentCalendar;
